import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track } from '../../utils/tracking';

export default function TermLoansPage() {
  useEffect(() => {
    track('page_view', { page: 'term_loans_detail' });
  }, []);

  useEffect(() => {
    document.title = 'Term Loans for Business | Fixed-Amount Business Financing';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn about term loans for business - fixed-amount financing with structured repayment for major investments, expansion, equipment purchases, and growth initiatives. Educational guide to business term loans.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'term loans, business term loans, fixed business loans, business expansion loans, equipment loans, business growth financing');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);
  }, []);

  const loanTypes = [
    {
      title: 'Short-Term Loans',
      term: '3-18 months',
      icon: 'ri-flashlight-line',
      description: 'Quick access to capital for immediate needs with faster repayment schedules.',
      bestFor: ['Inventory purchases', 'Seasonal cash flow', 'Quick opportunities', 'Bridge financing'],
      color: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Medium-Term Loans',
      term: '1-5 years',
      icon: 'ri-time-line',
      description: 'Balanced financing for moderate investments with manageable monthly payments.',
      bestFor: ['Equipment purchases', 'Business expansion', 'Marketing campaigns', 'Technology upgrades'],
      color: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Long-Term Loans',
      term: '5-10+ years',
      icon: 'ri-calendar-check-line',
      description: 'Major capital for significant investments with lower monthly payments spread over time.',
      bestFor: ['Real estate purchases', 'Major renovations', 'Business acquisitions', 'Large-scale expansion'],
      color: 'from-purple-500 to-pink-500'
    }
  ];

  const qualifications = [
    {
      icon: 'ri-time-line',
      title: 'Time in Business',
      requirement: '24+ months preferred',
      description: 'Most lenders require at least 2 years of operating history. Longer track records (3-5+ years) qualify for larger amounts and better terms.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Annual Revenue',
      requirement: '$250,000+ minimum',
      description: 'Revenue requirements vary by loan size. Larger loans require higher revenue. Lenders look for consistent or growing revenue patterns.'
    },
    {
      icon: 'ri-credit-card-line',
      title: 'Credit Score',
      requirement: '650+ personal credit',
      description: 'Personal credit score of 650+ typically required, with 680+ preferred for larger loans. Business credit is also evaluated if available.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Profitability',
      requirement: 'Positive cash flow',
      description: 'Demonstrated profitability and strong cash flow to support debt payments. Lenders review profit margins and debt-to-income ratios.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Collateral',
      requirement: 'May be required',
      description: 'Larger term loans often require collateral such as equipment, real estate, or other business assets to secure the loan.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Documentation',
      requirement: 'Extensive financials',
      description: 'Tax returns (2-3 years), financial statements, bank statements, and business plan may be required for larger loan amounts.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Pre-Qualification',
      timeline: '15-30 minutes',
      description: 'Complete a detailed application with business information, financial details, and loan purpose. Receive initial feedback on potential loan amounts and terms.',
      icon: 'ri-file-list-3-line'
    },
    {
      step: '02',
      title: 'Document Submission',
      timeline: '3-5 days',
      description: 'Gather and submit required financial documents including tax returns, financial statements, bank statements, and business plans. Digital submission speeds up the process.',
      icon: 'ri-folder-upload-line'
    },
    {
      step: '03',
      title: 'Underwriting Review',
      timeline: '5-14 days',
      description: 'Lenders conduct thorough review of financials, credit, collateral, and business viability. May request additional documentation or clarification during this phase.',
      icon: 'ri-search-eye-line'
    },
    {
      step: '04',
      title: 'Approval & Funding',
      timeline: '3-7 days',
      description: 'Once approved, review and sign loan agreements. Funds are typically disbursed within 3-7 days after final approval and document signing.',
      icon: 'ri-check-double-line'
    }
  ];

  const prosAndCons = {
    pros: [
      'Fixed monthly payments make budgeting predictable',
      'Larger loan amounts available ($50K-$5M+)',
      'Longer repayment terms reduce monthly payment burden',
      'Competitive interest rates for qualified businesses',
      'Build business credit with on-time payments',
      'Funds can be used for various business purposes',
      'May offer tax-deductible interest payments'
    ],
    cons: [
      'Requires extensive documentation and financial review',
      'Longer approval process (2-4 weeks typical)',
      'May require collateral for larger amounts',
      'Personal guarantee typically required',
      'Prepayment penalties may apply with some lenders',
      'Stricter qualification requirements than short-term options',
      'Fixed repayment regardless of business performance'
    ]
  };

  const useCases = [
    {
      icon: 'ri-tools-line',
      title: 'Equipment Purchases',
      description: 'Finance machinery, vehicles, technology, or other equipment needed to operate or expand your business.',
      example: 'A manufacturing company secures a $200K term loan to purchase new production equipment, increasing capacity by 40%.'
    },
    {
      icon: 'ri-building-line',
      title: 'Business Expansion',
      description: 'Fund new locations, facility renovations, or geographic expansion to grow your business footprint.',
      example: 'A restaurant chain obtains a $500K term loan to open two new locations in neighboring cities.'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'Inventory Investment',
      description: 'Purchase large inventory quantities for seasonal demand or to take advantage of bulk pricing discounts.',
      example: 'A retail business secures a $150K term loan to stock inventory for the holiday season, their busiest period.'
    },
    {
      icon: 'ri-exchange-dollar-line',
      title: 'Debt Refinancing',
      description: 'Consolidate multiple high-interest debts into a single term loan with better rates and terms.',
      example: 'A business refinances $300K in merchant cash advances and credit cards into a single term loan, reducing monthly payments by 35%.'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Business Acquisition',
      description: 'Finance the purchase of another business, including assets, customer base, and intellectual property.',
      example: 'A company secures a $1M term loan to acquire a competitor, immediately expanding market share and revenue.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Marketing & Growth',
      description: 'Invest in major marketing campaigns, technology infrastructure, or other growth initiatives.',
      example: 'A SaaS company obtains a $250K term loan to fund a comprehensive marketing campaign and hire additional sales staff.'
    }
  ];

  const faqs = [
    {
      question: 'What is the difference between a term loan and a line of credit?',
      answer: 'A term loan provides a lump sum of capital upfront that you repay over a fixed period with regular payments. A line of credit provides access to a credit limit that you can draw from as needed, only paying interest on what you use. Term loans are better for one-time investments, while lines of credit are better for ongoing, flexible funding needs.'
    },
    {
      question: 'How much can I borrow with a business term loan?',
      answer: 'Business term loans typically range from $50,000 to $5,000,000 depending on your business size, revenue, creditworthiness, and loan purpose. Smaller businesses may qualify for $50K-$250K, while established businesses with strong financials can access $500K-$5M+. The amount you qualify for depends on your ability to repay based on cash flow and financial strength.'
    },
    {
      question: 'What credit score do I need for a term loan?',
      answer: 'Most lenders require a personal credit score of 650 or higher for term loans, with 680+ preferred for larger amounts and better interest rates. Business credit is also evaluated if available. Stronger credit profiles qualify for higher loan amounts, lower rates, and better terms. Some alternative lenders may work with scores as low as 600 but typically offer higher rates.'
    },
    {
      question: 'How long does it take to get approved for a term loan?',
      answer: 'The term loan process typically takes 2-4 weeks from application to funding. Pre-qualification takes 15-30 minutes, document gathering takes 3-5 days, underwriting review takes 5-14 days, and funding occurs within 3-7 days after approval. The timeline varies based on loan size, complexity, and how quickly you provide required documentation.'
    },
    {
      question: 'What documents are required for a term loan application?',
      answer: 'Term loans require extensive documentation including: 2-3 years of business tax returns, 2-3 years of personal tax returns, business financial statements (profit & loss, balance sheet), 6-12 months of business bank statements, business plan (for larger amounts), collateral documentation (if applicable), business license, and legal documents. Having these ready speeds up the process.'
    },
    {
      question: 'Do I need collateral for a term loan?',
      answer: 'Collateral requirements depend on loan size and lender. Smaller term loans ($50K-$100K) may be unsecured or require only a personal guarantee. Larger loans ($250K+) often require collateral such as equipment, real estate, inventory, or other business assets. Secured loans typically offer lower interest rates and higher loan amounts than unsecured options.'
    },
    {
      question: 'What interest rates can I expect on a term loan?',
      answer: 'Term loan interest rates vary widely based on creditworthiness, loan size, term length, and lender type. Traditional bank loans may offer rates from 6-12% for well-qualified borrowers. Alternative lenders typically charge 10-30% depending on risk factors. Longer terms and larger amounts generally have lower rates. Your specific rate depends on your financial profile and business strength.'
    },
    {
      question: 'Can I pay off a term loan early?',
      answer: 'Many term loans allow early repayment, but some lenders charge prepayment penalties to recoup lost interest. Prepayment penalties typically range from 1-5% of the remaining balance or a certain number of months of interest. Always review loan terms carefully and ask about prepayment policies before signing. Some lenders offer no prepayment penalty options.'
    },
    {
      question: 'What can I use a term loan for?',
      answer: 'Term loans can be used for most business purposes including equipment purchases, business expansion, inventory, debt refinancing, business acquisitions, marketing campaigns, technology investments, working capital, and more. Some lenders may restrict use for certain purposes like real estate (which may require specialized CRE loans) or speculative investments. Always clarify allowed uses with your lender.'
    },
    {
      question: 'How are term loan payments structured?',
      answer: 'Term loans typically have fixed monthly payments that include both principal and interest, similar to a mortgage. Payments remain the same throughout the loan term, making budgeting predictable. Some lenders offer interest-only periods at the beginning, followed by principal and interest payments. Payment amounts depend on loan size, interest rate, and term length.'
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <Link to="/business-funding" className="text-gray-600 hover:text-blue-600 transition-colors">
                Business Funding
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <span className="text-gray-900 font-semibold">Term Loans</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-calendar-check-line"></i>
                <span>Fixed-Amount Business Financing</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Business Term Loans
              </h1>
              
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Term loans provide fixed amounts of capital with structured repayment schedules, ideal for major investments, expansion projects, equipment purchases, and long-term business growth initiatives.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100">
                  <div className="text-2xl font-bold text-blue-600 mb-1">$50K-$5M</div>
                  <div className="text-sm text-gray-600">Loan Amount</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100">
                  <div className="text-2xl font-bold text-blue-600 mb-1">2-4 weeks</div>
                  <div className="text-sm text-gray-600">Approval Time</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100">
                  <div className="text-2xl font-bold text-blue-600 mb-1">1-10 years</div>
                  <div className="text-sm text-gray-600">Loan Term</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100">
                  <div className="text-2xl font-bold text-blue-600 mb-1">650+</div>
                  <div className="text-sm text-gray-600">Min Credit Score</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/prequalification"
                  onClick={() => track('cta_click', { location: 'term_loans_hero', action: 'prequalification' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Check Your Options
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg border-2 border-blue-200 hover:border-blue-300 hover:bg-blue-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Educational Notice */}
        <section className="bg-amber-50 border-y border-amber-200 py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-amber-700 text-xl"></i>
              </div>
              <div className="text-sm text-amber-900">
                <strong>Educational platform. Not a lender.</strong> Information about business term loans for educational purposes. No guarantee of approval. Eligibility varies by lender and product.
              </div>
            </div>
          </div>
        </section>

        {/* What Are Term Loans */}
        <section id="how-it-works" className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
                  Overview
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  What Are Business Term Loans?
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  A business term loan provides a lump sum of capital that is repaid over a fixed period (typically 1-10 years) with regular monthly payments. These loans are ideal for significant investments like equipment purchases, facility expansion, or major business initiatives that generate returns over time.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Unlike revolving credit lines, term loans provide all funds upfront with a structured repayment schedule. This makes budgeting predictable and is well-suited for one-time investments rather than ongoing operational needs.
                </p>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-blue-600"></i>
                    Key Characteristics
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                      <span>Fixed monthly payments for predictable budgeting</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                      <span>Lump sum disbursed upfront for immediate use</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                      <span>Terms ranging from 1-10 years based on loan purpose</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                      <span>Competitive rates for qualified businesses</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/eaf7dc9c2f0fa1ade3e7b9736c76e7d4.jpg"
                  alt="Business owner reviewing term loan documents"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Types of Term Loans */}
        <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
                Loan Types
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Types of Term Loans
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Term loans come in different lengths to match your business needs and investment timeline
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {loanTypes.map((type, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all">
                  <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${type.color} rounded-xl mb-6 shadow-lg`}>
                    <i className={`${type.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.title}</h3>
                  <div className="text-blue-600 font-semibold mb-4">{type.term}</div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{type.description}</p>
                  <div>
                    <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Best For:</div>
                    <ul className="space-y-2">
                      {type.bestFor.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <i className="ri-arrow-right-s-line text-blue-600 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Qualification Requirements */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
                Requirements
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Typical Qualification Requirements
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding what lenders look for helps you prepare for the application process
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {qualifications.map((qual, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 flex items-center justify-center bg-blue-50 rounded-lg mb-6">
                    <i className={`${qual.icon} text-blue-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{qual.title}</h3>
                  <div className="text-blue-600 font-semibold text-sm mb-3">{qual.requirement}</div>
                  <p className="text-gray-600 leading-relaxed">{qual.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-4">
                Process
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Application Process Timeline
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the typical timeline from application to funding
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-2xl font-bold rounded-xl flex-shrink-0 shadow-lg">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <i className={`${step.icon} text-blue-600`}></i>
                            {step.title}
                          </h3>
                          <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-4 py-1 rounded-full whitespace-nowrap">
                            {step.timeline}
                          </span>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 text-sm font-semibold rounded-full mb-4">
                Use Cases
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Can You Use Term Loans For?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Term loans are versatile financing tools for major business investments and growth initiatives
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl mb-6 shadow-lg">
                    <i className={`${useCase.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{useCase.description}</p>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Example:</div>
                    <p className="text-sm text-gray-700 leading-relaxed">{useCase.example}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
                Considerations
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pros & Cons of Term Loans
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the advantages and considerations helps you make informed decisions
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Pros */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-xl">
                    <i className="ri-thumb-up-line text-emerald-600 text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Advantages</h3>
                </div>
                <ul className="space-y-4">
                  {prosAndCons.pros.map((pro, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-emerald-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cons */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl">
                    <i className="ri-alert-line text-gray-600 text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Considerations</h3>
                </div>
                <ul className="space-y-4">
                  {prosAndCons.cons.map((con, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-gray-600 text-sm"></i>
                      </div>
                      <span className="text-gray-600 leading-relaxed">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
                FAQ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600">
                Common questions about business term loans
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden group hover:border-blue-300 transition-colors"
                >
                  <summary className="px-8 py-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                    <i className="ri-add-line text-2xl text-blue-600 group-open:rotate-45 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-8 pb-6 text-gray-600 leading-relaxed border-t border-gray-100 pt-6">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Explore Term Loan Options?
            </h2>
            <p className="text-xl text-blue-50 mb-10 max-w-2xl mx-auto leading-relaxed">
              Complete our pre-qualification form to see what term loan options may be available for your business. No credit impact to explore your options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/prequalification"
                onClick={() => track('cta_click', { location: 'term_loans_bottom', action: 'prequalification' })}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
              >
                Start Pre-Qualification
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/business-funding"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                View All Funding Types
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
